import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, FileText, Phone, Mail, Sparkles } from "lucide-react";
import { company } from "../mock";

const ServiceQuoteBlock = ({ data }) => {
  return (
    <div className="space-y-8">
      {/* Hero block - call out for personalized quote */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B2545] via-[#0B2545] to-[#0a1f3d] p-8 md:p-12 text-white shadow-xl">
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#00A859]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#00A859]/10 rounded-full blur-3xl" />

        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#00A859] px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5 shadow-lg">
              <Sparkles className="w-3.5 h-3.5" /> Servizio su misura
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-4">
              {data.headline}
            </h2>
            <p className="text-gray-200 text-base md:text-lg leading-relaxed mb-7 max-w-2xl">
              {data.description}
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5 mb-8">
              {data.bullets.map((b) => (
                <div key={b} className="flex items-start gap-2.5 text-sm">
                  <span className="w-5 h-5 rounded-full bg-[#00A859] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  <span className="text-gray-100">{b}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contatti"
                className="group inline-flex items-center gap-2 bg-[#00A859] hover:bg-[#00874A] text-white font-bold px-7 py-4 rounded-full text-sm uppercase tracking-wider transition-all hover:shadow-xl hover:shadow-[#00A859]/30"
              >
                <FileText className="w-4 h-4" /> Richiedi preventivo gratuito
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={`tel:${company.telRaw}`}
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-7 py-4 rounded-full text-sm uppercase tracking-wider transition-all backdrop-blur-sm"
              >
                <Phone className="w-4 h-4" /> {company.tel}
              </a>
            </div>
          </div>

          {/* Right Quote card */}
          <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-6 md:p-7">
            <div className="text-[#00A859] text-xs font-bold uppercase tracking-[3px] mb-2">
              Come funziona
            </div>
            <h3 className="text-xl font-bold mb-5">In 3 semplici passi</h3>
            <ol className="space-y-4">
              {[
                { n: "01", t: "Contattaci", d: "Compila il modulo o chiamaci per descrivere le tue esigenze." },
                { n: "02", t: "Sopralluogo gratuito", d: "Veniamo da te per valutare gli interventi necessari." },
                { n: "03", t: "Preventivo dedicato", d: "Riceverai un\u2019offerta dettagliata e personalizzata." },
              ].map((s) => (
                <li key={s.n} className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#00A859] text-white font-extrabold text-sm flex items-center justify-center shrink-0">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm">{s.t}</div>
                    <div className="text-xs text-gray-300 leading-relaxed mt-0.5">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="mt-6 pt-5 border-t border-white/15 space-y-2 text-sm">
              <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-gray-200 hover:text-[#00A859] transition-colors break-all">
                <Mail className="w-4 h-4 text-[#00A859]" /> {company.email}
              </a>
              <a href={`tel:${company.telRaw}`} className="flex items-center gap-2 text-gray-200 hover:text-[#00A859] transition-colors">
                <Phone className="w-4 h-4 text-[#00A859]" /> {company.tel}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Services grid */}
      <div>
        <h3 className="text-2xl font-extrabold text-[#0B2545] mb-1 tracking-tight">
          Cosa includiamo
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Personalizziamo ogni servizio in base alle tue esigenze operative.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.services.map((s) => (
            <div
              key={s.title}
              className="group bg-white border border-gray-100 hover:border-[#00A859] rounded-2xl p-5 hover:shadow-md transition-all"
            >
              <div className="w-10 h-10 rounded-xl bg-[#00A859]/10 flex items-center justify-center mb-3 group-hover:bg-[#00A859] transition-colors">
                <Check className="w-5 h-5 text-[#00A859] group-hover:text-white transition-colors" />
              </div>
              <h4 className="font-bold text-[#0B2545] text-base mb-1.5">{s.title}</h4>
              <p className="text-xs text-gray-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA strip */}
      <div className="bg-gradient-to-r from-[#00A859] to-[#00874A] rounded-3xl p-7 md:p-9 text-white flex flex-col md:flex-row items-center justify-between gap-5">
        <div>
          <h4 className="text-xl md:text-2xl font-extrabold mb-1">Pronto per iniziare?</h4>
          <p className="text-white/90 text-sm">
            Contattaci oggi: riceverai il preventivo entro 48/72 ore dal sopralluogo.
          </p>
        </div>
        <Link
          to="/contatti"
          className="inline-flex items-center gap-2 bg-white text-[#00874A] font-bold px-6 py-3.5 rounded-full text-sm uppercase tracking-wider hover:bg-gray-100 transition-colors"
        >
          Contattaci ora <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default ServiceQuoteBlock;
